import { useEffect, useState } from "react";

import type { HerstellerUebersicht, KonfigurationsZustand, ProduktInfo } from "@repo/shared";

import { api } from "../../lib/api";
import { ProduktKonfigurator } from "../ProduktKonfigurator";
import { Blatt } from "./Blatt";

/** Hersteller, deren Konfigurator-Preis die Objektkalkulation versteht. */
const SCHIENEN_HERSTELLER: Record<string, "interstil" | "erfal"> = { interstil: "interstil", erfal: "erfal" };

export type SchienenWahl = {
  produkt: { id: string; name: string; hersteller: string };
  /** BVK je Schiene (Interstil-Listenpreis brutto). */
  bvk: number;
  /** NEK je Schiene (Hersteller-EK netto). */
  nek: number;
  rechner: "interstil" | "erfal";
};

/**
 * Schiene aus den Produkten der Konfigurator-API: Hersteller, Produkt suchen,
 * im Konfigurator mit vorbelegter Länge bepreisen, Preis übernehmen.
 */
export function SchienenAuswahl({ offen, onClose, onWahl, dekolaengeCm }: { offen: boolean; onClose: () => void; onWahl: (w: SchienenWahl) => void; dekolaengeCm: number }) {
  const [hersteller, setHersteller] = useState<HerstellerUebersicht[]>([]);
  const [herstellerKey, setHerstellerKey] = useState("interstil");
  const [suche, setSuche] = useState("");
  const [produkte, setProdukte] = useState<ProduktInfo[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [produkt, setProdukt] = useState<ProduktInfo | null>(null);
  const [stand, setStand] = useState<KonfigurationsZustand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!offen) return;
    void api.api.admin.katalog.hersteller.$get().then(async (res) => {
      if (!res.ok) return setFehler("Hersteller nicht ladbar");
      const daten = await res.json();
      if ("hersteller" in daten) {
        const passende = daten.hersteller.filter((h) => h.schluessel in SCHIENEN_HERSTELLER);
        setHersteller(passende);
        if (passende.length && !passende.some((h) => h.schluessel === herstellerKey)) setHerstellerKey(passende[0]!.schluessel);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  useEffect(() => {
    if (!offen || !herstellerKey) return;
    let aktiv = true;
    void api.api.admin.katalog.hersteller[":hersteller"].produkte
      .$get({ param: { hersteller: herstellerKey }, query: { suche: suche || undefined, limit: "40" } })
      .then(async (res) => {
        if (!aktiv) return;
        if (!res.ok) return setFehler("Produkte nicht ladbar");
        const daten = await res.json();
        if ("produkte" in daten) {
          setProdukte(daten.produkte);
          setGesamt(daten.gesamt);
          setFehler(null);
        }
      });
    return () => {
      aktiv = false;
    };
  }, [offen, herstellerKey, suche]);

  const preis = stand?.ergebnis?.gueltig ? stand.ergebnis.preis : null;

  function uebernehmen() {
    if (!produkt || !preis) return;
    onWahl({
      produkt: { id: produkt.id, name: produkt.name, hersteller: herstellerKey },
      bvk: preis.listenpreisBruttoCents / 100,
      nek: preis.ekNettoCents / 100,
      rechner: SCHIENEN_HERSTELLER[herstellerKey] ?? "interstil",
    });
    onClose();
  }

  return (
    <Blatt offen={offen} onClose={onClose} titel={produkt ? `Schiene: ${produkt.name}` : "Schiene aus Produkten"}>
      {!produkt ? (
        <div className="kalk-auswahl">
          <div className="kalk-auswahl__kopf">
            <select value={herstellerKey} onChange={(e) => setHerstellerKey(e.target.value)} aria-label="Hersteller">
              {hersteller.map((h) => (
                <option key={h.schluessel} value={h.schluessel}>{h.name} · {h.anzahlProdukte} Produkte</option>
              ))}
              {hersteller.length === 0 && <option value="">Kein Schienenhersteller mit Konfigurator aktiv</option>}
            </select>
            <input type="search" className="kalk-auswahl__suche" placeholder="Produkt suchen, z. B. Schiene, Wave, Rundstore …" value={suche} onChange={(e) => setSuche(e.target.value)} autoFocus />
          </div>
          {fehler && <p className="kalk-fehler">{fehler}</p>}
          <ul className="kalk-auswahl__liste">
            {produkte.map((p) => (
              <li key={p.id}>
                <button type="button" className="kalk-auswahl__eintrag" onClick={() => { setStand(null); setProdukt(p); }}>
                  <span className="kalk-auswahl__titel">{p.name}{p.variante && <small> {p.variante}</small>}</span>
                  <span className="kalk-auswahl__meta">{p.kategorieName}{p.beschreibung ? ` · ${p.beschreibung}` : ""}</span>
                </button>
              </li>
            ))}
            {produkte.length === 0 && !fehler && <li className="muted small">Keine Produkte gefunden.</li>}
          </ul>
          {gesamt > produkte.length && <p className="muted small">{gesamt} Treffer, die ersten {produkte.length} angezeigt. Suche eingrenzen.</p>}
        </div>
      ) : (
        <div className="kalk-schiene-konfi">
          <div className="kalk-schiene-konfi__leiste">
            <button type="button" className="button" onClick={() => setProdukt(null)}>← Andere Schiene</button>
            <span className="muted small">Länge aus der Dekolänge vorbelegt: {dekolaengeCm} cm</span>
            <button type="button" className="button kalk-knopf--primaer" disabled={!preis} onClick={uebernehmen}>
              {preis ? `Übernehmen · BVK ${(preis.listenpreisBruttoCents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}` : "Noch kein Preis"}
            </button>
          </div>
          <ProduktKonfigurator hersteller={herstellerKey} produktId={produkt.id} startWerte={{ laengeMm: Math.round(dekolaengeCm * 10) }} onErgebnis={setStand} />
        </div>
      )}
    </Blatt>
  );
}
